import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { authApi, userApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import bg2 from '../assets/images/bg2.png';
import logoIzyko from '../assets/logos/izyko-black.png';
import { Loader2, Eye, EyeOff, ArrowLeft, Mail, AlertTriangle } from 'lucide-react';

export default function RegisterPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { login } = useAuth();
  
  const [step, setStep] = useState('form'); // 'form' or 'verify'
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    first_name: '',
    last_name: '',
    phone: '',
    department: '',
    city: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [verificationCode, setVerificationCode] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(0);

  useEffect(() => {
    fetchDepartments();
  }, []);

  useEffect(() => {
    if (resendCountdown > 0) {
      const timer = setTimeout(() => setResendCountdown(resendCountdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCountdown]);

  const fetchDepartments = async () => {
    try {
      const response = await userApi.getDepartments();
      setDepartments(response.data.departments);
    } catch (error) {
      console.error('Error fetching departments:', error);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }

    setLoading(true);

    try {
      const { confirmPassword, ...registerData } = formData;
      const response = await authApi.register(registerData);
      
      if (response.data.requires_verification) {
        setStep('verify');
        setResendCountdown(60);
        toast.success('Un code de vérification a été envoyé à votre email');
      }
    } catch (error) {
      const message = error.response?.data?.detail || t('errors.generic');
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    
    if (verificationCode.length !== 6) {
      toast.error('Le code doit contenir 6 chiffres');
      return;
    }

    setLoading(true);

    try {
      const response = await authApi.verifyRegistration(formData.email, verificationCode);
      
      // Store tokens and login
      localStorage.setItem('access_token', response.data.access_token);
      localStorage.setItem('refresh_token', response.data.refresh_token);
      
      toast.success('Inscription réussie !');
      navigate('/dashboard');
    } catch (error) {
      const message = error.response?.data?.detail || 'Code invalide';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setResendLoading(true);
    try {
      await authApi.resendVerificationCode(formData.email);
      setResendCountdown(60);
      toast.success('Nouveau code envoyé');
    } catch (error) {
      toast.error('Erreur lors de l\'envoi du code');
    } finally {
      setResendLoading(false);
    }
  };

  // Verification step
  if (step === 'verify') {
    return (
      <div className="min-h-screen flex flex-col lg:flex-row">
        {/* Mobile header */}
        <div className="lg:hidden p-4 flex items-center">
          <Button variant="ghost" size="icon" className="rounded-full" onClick={() => setStep('form')}>
            <ArrowLeft className="h-5 w-5" strokeWidth={2} />
          </Button>
        </div>

        {/* Left side - Image */}
        <div 
          className="hidden lg:block lg:w-5/12 bg-cover bg-center relative"
          style={{
            backgroundImage: `url(${bg2})`
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-l from-background/20 to-transparent" />
        </div>

        {/* Right side - Verification Form */}
        <div className="flex-1 flex items-center justify-center p-6 md:p-8 lg:p-12">
          <div className="w-full max-w-md">
            <Link to="/" className="hidden lg:inline-block mb-8">
              <img src={logoIzyko} alt="izyko" className="h-12 w-auto object-contain" />
            </Link>

            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Mail className="h-8 w-8 text-primary" strokeWidth={1.5} />
              </div>
              <h1 className="font-heading text-3xl font-black mb-2">
                Vérifiez votre email
              </h1>
              <p className="text-muted-foreground font-medium">
                Un code de vérification a été envoyé à
              </p>
              <p className="text-foreground font-bold mt-1">{formData.email}</p>
            </div>

            <div className="flex items-start gap-2 p-4 bg-yellow-50 border-2 border-yellow-200 rounded-xl">
              <AlertTriangle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" strokeWidth={2} />
              <p className="text-sm text-yellow-800">
                Si vous ne trouvez pas le mail, <strong>vérifiez vos spams</strong>.
              </p>
              <p className="text-sm text-yellow-800">
                <strong>Verifye imèl ki nan spam yo tou !</strong>.
              </p>
            </div>

            <form onSubmit={handleVerifyCode} className="space-y-6">
              <div className="space-y-2">
                <Label className="font-bold">Code de vérification</Label>
                <Input
                  type="text"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  maxLength={6}
                  className="text-center text-3xl font-mono tracking-[0.5em] h-16"
                  data-testid="verification-code-input"
                />
              </div>

              <Button 
                type="submit" 
                className="w-full rounded-full py-6 text-base"
                disabled={loading || verificationCode.length !== 6}
                data-testid="verify-code-btn"
              >
                {loading ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  'Vérifier et créer mon compte'
                )}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-sm text-muted-foreground mb-2">
                Vous n'avez pas reçu le code ?
              </p>
              <Button
                variant="outline"
                className="rounded-full"
                onClick={handleResendCode}
                disabled={resendLoading || resendCountdown > 0}
              >
                {resendLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : null}
                {resendCountdown > 0 ? `Renvoyer dans ${resendCountdown}s` : 'Renvoyer le code'}
              </Button>
            </div>

            <p className="mt-6 text-center text-sm text-muted-foreground">
              <button
                type="button"
                onClick={() => setStep('form')}
                className="text-foreground font-bold hover:underline"
              >
                Modifier l'email
              </button>
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Registration form
  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      {/* Mobile header */}
      <div className="lg:hidden p-4 flex items-center">
        <Button variant="ghost" size="icon" className="rounded-full" onClick={() => navigate('/')}>
          <ArrowLeft className="h-5 w-5" strokeWidth={2} />
        </Button>
      </div>

      {/* Left side - Image */}
      <div 
        className="hidden lg:block lg:w-5/12 bg-cover bg-center relative"
        style={{
          backgroundImage: `url(${bg2})`
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-l from-background/20 to-transparent" />
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex items-center justify-center p-6 md:p-8 lg:p-12 overflow-y-auto">
        <div className="w-full max-w-md py-4">
          <Link to="/" className="hidden lg:inline-block mb-8">
            <img src={logoIzyko} alt="izyko" className="h-12 w-auto object-contain" />
          </Link>

          <div className="text-center lg:text-left mb-8">
            <h1 className="font-heading text-3xl md:text-4xl font-black mb-2">
              {t('auth.register.title')}
            </h1>
            <p className="text-muted-foreground font-medium">
              {t('auth.register.subtitle')}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="font-bold">{t('auth.register.firstName')}</Label>
                <Input
                  value={formData.first_name}
                  onChange={(e) => handleChange('first_name', e.target.value)}
                  required
                  data-testid="register-firstname-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="font-bold">{t('auth.register.lastName')}</Label>
                <Input
                  value={formData.last_name}
                  onChange={(e) => handleChange('last_name', e.target.value)}
                  required
                  data-testid="register-lastname-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="font-bold">{t('auth.register.email')}</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                placeholder="vous@example.com"
                required
                data-testid="register-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label className="font-bold">{t('auth.register.phone')}</Label>
              <Input
                type="tel"
                value={formData.phone}
                onChange={(e) => handleChange('phone', e.target.value)}
                placeholder="+509 0000 0000"
                data-testid="register-phone-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="font-bold">{t('auth.register.department')}</Label>
                <Select 
                  value={formData.department}
                  onValueChange={(value) => handleChange('department', value)}
                >
                  <SelectTrigger className="rounded-xl h-12" data-testid="register-department-select">
                    <SelectValue placeholder="Sélectionner" />
                  </SelectTrigger>
                  <SelectContent className="rounded-xl">
                    {departments.map((dept) => (
                      <SelectItem key={dept} value={dept} className="rounded-lg">
                        {dept}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="font-bold">{t('auth.register.city')}</Label>
                <Input
                  value={formData.city}
                  onChange={(e) => handleChange('city', e.target.value)}
                  data-testid="register-city-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="font-bold">{t('auth.register.password')}</Label>
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => handleChange('password', e.target.value)}
                  required
                  className="pr-12"
                  data-testid="register-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5" strokeWidth={2} />
                  ) : (
                    <Eye className="h-5 w-5" strokeWidth={2} />
                  )}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="font-bold">{t('auth.register.confirmPassword')}</Label>
              <Input
                type={showPassword ? 'text' : 'password'}
                value={formData.confirmPassword}
                onChange={(e) => handleChange('confirmPassword', e.target.value)}
                required
                data-testid="register-confirm-password-input"
              />
            </div>

            <Button 
              type="submit" 
              className="w-full rounded-full py-6 text-base mt-6"
              disabled={loading}
              data-testid="register-submit-btn"
            >
              {loading ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                t('auth.register.submit')
              )}
            </Button>
          </form>

          <p className="mt-6 text-center text-sm text-muted-foreground font-medium">
            {t('auth.register.hasAccount')}{' '}
            <Link to="/login" className="text-foreground font-bold hover:underline">
              {t('auth.register.login')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
